import React, { useEffect, useState } from 'react'
import { clientDatabasesAPI } from '@hpanel/web-common/api/client'
import type {
  ClientDatabase,
  ClientDatabaseDeleteResponse,
  ClientDatabaseUser,
} from '@hpanel/web-common/api/types'

/**
 * Confirmation dialog for cascade-dropping a database.
 *
 * Why two extra friction layers:
 *   1. We pre-fetch the user list via `getOne` and render it so the
 *      tenant sees exactly which DB accounts will be dropped along
 *      with the database. Surprise dropped-user incidents in the
 *      audit log motivated this.
 *   2. Type-to-confirm gate — the user must type the *full* database
 *      name (server-prefixed) before the Delete button enables. This
 *      stops fat-finger destruction on a row click.
 *
 * §9: errors come from fetchAPI verbatim (incl. server detail). On
 * success we surface the response payload (`deleted_db_name` +
 * `dropped_users`) up to the parent so the page can toast it.
 */

export interface DeleteDatabaseDialogProps {
  database: ClientDatabase
  onClose: () => void
  onDeleted: (result: ClientDatabaseDeleteResponse) => void
}

export const DeleteDatabaseDialog: React.FC<DeleteDatabaseDialogProps> = ({
  database,
  onClose,
  onDeleted,
}) => {
  const [users, setUsers] = useState<ClientDatabaseUser[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [confirmName, setConfirmName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const detail = await clientDatabasesAPI.getOne(database.id)
        if (!cancelled) setUsers(detail.users)
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[DeleteDatabaseDialog] getOne failed', e)
          setLoadError(
            e instanceof Error ? e.message : 'Could not load DB users',
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [database.id])

  const canConfirm = confirmName === database.name && !busy

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canConfirm) return
    setBusy(true)
    setError(null)
    try {
      const result = await clientDatabasesAPI.delete(database.id)
      onDeleted(result)
      onClose()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[DeleteDatabaseDialog] delete failed', e)
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-testid="delete-db-dialog"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
        <h2 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">
          Delete Database
        </h2>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          You are about to drop{' '}
          <span className="font-mono font-semibold">{database.name}</span>.
          This is permanent.
        </p>

        <div className="mt-4 mb-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            The following DB users will also be dropped:
          </p>
          {loadError && (
            <p
              data-testid="delete-users-load-error"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {loadError}
            </p>
          )}
          {!loadError && users == null && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </p>
          )}
          {!loadError && users != null && users.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              (none)
            </p>
          )}
          {!loadError && users != null && users.length > 0 && (
            <ul
              data-testid="delete-cascaded-users"
              className="text-sm font-mono bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 max-h-32 overflow-y-auto"
            >
              {users.map((u) => (
                <li
                  key={u.id}
                  data-testid={`delete-cascade-user-${u.id}`}
                  className="text-gray-800 dark:text-gray-200"
                >
                  {u.username}
                  {u.is_primary && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                      (primary)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div
            data-testid="delete-error"
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type{' '}
              <span className="font-mono font-semibold">{database.name}</span>{' '}
              to confirm
            </label>
            <input
              type="text"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              data-testid="delete-confirm-name"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder={database.name}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="delete-cancel"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canConfirm}
              data-testid="delete-submit"
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? 'Deleting…' : 'Delete Database'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
